import React, { useState, useRef, useEffect, CSSProperties } from "react";

interface DateRangePickerProps {
  onDateRangeChange: (startDate: Date | null, endDate: Date | null) => void;
  initialStartDate?: Date | null;
  initialEndDate?: Date | null;
  showDefaultDates?: boolean; // If false, don't set default dates when initial dates are null
}

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatDate(date: Date): string {
  const day = date.getDate();
  const month = MONTHS[date.getMonth()].slice(0, 3);
  const year = date.getFullYear();
  const suffix = getDaySuffix(day);
  return `${month} ${day}${suffix}, ${year}`;
}

function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function isSameDay(date1: Date | null, date2: Date | null): boolean {
  if (!date1 || !date2) return false;
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
}

function isInRange(date: Date, start: Date | null, end: Date | null): boolean {
  if (!start || !end) return false;
  const time = date.getTime();
  return time > start.getTime() && time < end.getTime();
}

function isFutureDate(date: Date): boolean {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return date.getTime() > today.getTime();
}

function isBeforeDate(date: Date, compareDate: Date | null): boolean {
  if (!compareDate) return false;
  return date.getTime() < compareDate.getTime();
}

function CalendarIcon({
  size = 14,
  color = "#9ca3af",
}: {
  size?: number;
  color?: string;
}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 14 14"
      fill="none"
    >
      <path
        d="M13.44 1.26H10.5V0.14C10.5 0.063 10.437 0 10.36 0H9.38C9.303 0 9.24 0.063 9.24 0.14V1.26H4.76V0.14C4.76 0.063 4.697 0 4.62 0H3.64C3.563 0 3.5 0.063 3.5 0.14V1.26H0.56C0.25025 1.26 0 1.51025 0 1.82V13.44C0 13.7498 0.25025 14 0.56 14H13.44C13.7498 14 14 13.7498 14 13.44V1.82C14 1.51025 13.7498 1.26 13.44 1.26ZM12.74 12.74H1.26V6.09H12.74V12.74ZM1.26 4.9V2.52H3.5V3.36C3.5 3.437 3.563 3.5 3.64 3.5H4.62C4.697 3.5 4.76 3.437 4.76 3.36V2.52H9.24V3.36C9.24 3.437 9.303 3.5 9.38 3.5H10.36C10.437 3.5 10.5 3.437 10.5 3.36V2.52H12.74V4.9H1.26Z"
        fill={color}
      />
    </svg>
  );
}

function ChevronLeft({
  size = 16,
  color = "#6b7280",
}: {
  size?: number;
  color?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      fill="none"
      stroke={color}
      viewBox="0 0 24 24"
      strokeWidth={2}
    >
      <polyline points="15,18 9,12 15,6" />
    </svg>
  );
}

function ChevronRight({
  size = 16,
  color = "#6b7280",
}: {
  size?: number;
  color?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      fill="none"
      stroke={color}
      viewBox="0 0 24 24"
      strokeWidth={2}
    >
      <polyline points="9,6 15,12 9,18" />
    </svg>
  );
}

// Styles
const styles: Record<string, CSSProperties> = {
  container: {
    position: "relative",
    display: "inline-block",
    fontFamily: "'Poppins', sans-serif",
  },
  trigger: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    // width: "240px",
    height: "2.625rem",
    padding: "0 1rem",
    backgroundColor: "#ffffff",
    border: "1px solid #d0d5db",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "border-color 0.2s",
  },
  triggerText: {
    // flex: 1,
    textAlign: "center" as const,
    color: "#6b7280",
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: "1.25rem",
  },
  dropdown: {
    position: "absolute" as const,
    top: "100%",
    left: 0,
    marginTop: "0.8rem",
    width: "20rem",
    height: "15.875rem",
    backgroundColor: "#ffffff",
    borderRadius: "0.625rem",
    border: "1px solid #E5E7EB",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    zIndex: 50,
    animation: "fadeIn 0.2s ease-out",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    padding: "1.0625rem 1.0625rem 0 1.0625rem",
    gap: "1rem",
    transform: "translateY(0)",
  },
  dropdownContent: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "1rem",
    width: "100%",
    // flex: 1,
    minHeight: 0,
  },
  fieldLabel: {
    fontSize: "0.875rem",
    color: "#364153",
    fontFamily: "'Poppins', sans-serif",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "1.25rem",
    marginBottom: 0,
  },
  fieldInput: {
    display: "flex",
    alignItems: "center",
    padding: "0.5rem 1.25rem",
    border: "1px solid #D1D5DC",
    borderRadius: "0.625rem",
    justifyContent: "space-between",
    alignSelf: "stretch",
    cursor: "pointer",
  },
  fieldTextInput: {
    fontSize: "1rem",
    color: "#6B7280",
    textAlign: "left" as const,
    fontFamily: "'Poppins', sans-serif",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "1.51575rem",
    border: "none",
    outline: "none",
    background: "transparent",
    flex: 1,
    padding: 0,
  },
  calendarHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "8px",
  },
  navButton: {
    padding: "2px",
    borderRadius: "4px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  monthLabel: {
    fontSize: "11px",
    fontWeight: 500,
    color: "#354152",
  },
  daysGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "2px",
  },
  dayHeader: {
    textAlign: "center" as const,
    fontSize: "10px",
    color: "#9ca3af",
    fontWeight: 400,
    marginBottom: "4px",
  },
  dayCell: {
    width: "26px",
    height: "26px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "11px",
    borderRadius: "50%",
    cursor: "pointer",
    border: "none",
    backgroundColor: "transparent",
    transition: "background-color 0.15s, color 0.15s",
  },
  dayCellSelected: {
    backgroundColor: "#7e57c2",
    color: "#ffffff",
    fontWeight: 600,
  },
  dayCellInRange: {
    backgroundColor: "#ede7f6",
    color: "#7e57c2",
  },
  dayCellDefault: {
    color: "#354152",
  },
  dayCellDisabled: {
    color: "#d0d5db",
    cursor: "not-allowed",
  },
  errorText: {
    fontSize: "10px",
    color: "#ef4444",
    marginTop: "2px",
  },
  calendarPopup: {
    position: "absolute" as const,
    top: "100%",
    left: 0,
    marginTop: "0.8rem",
    minWidth: "max-content",
    width: "100%",
    maxWidth: "280px",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    border: "1px solid #d0d5db",
    boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
    zIndex: 100,
    padding: "8px",
    animation: "fadeIn 0.2s ease-out",
    transform: "translateY(0)",
    boxSizing: "border-box" as const,
  },
  fieldWrapper: {
    position: "relative" as const,
    width: "100%",
  },
  actionBar: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    gap: "0.5rem",
    height: "3.0625rem",
    paddingTop: "0.75rem",
    paddingBottom: "1rem",
    marginBottom: "0",

    paddingLeft: "1.0625rem",
    paddingRight: "0",
    borderTop: "1px solid rgba(0, 0, 0, 0.10)",
    flexShrink: 0,
    alignSelf: "stretch",
    // width: "calc(100% + 2.125rem)",
    boxSizing: "border-box" as const,
  },
  cancelButton: {
    display: "flex",
    width: "4.61619rem",
    height: "2.25rem",
    padding: "0.5rem 1rem",
    alignItems: "center",
    justifyContent: "center",
    color: "#364153",
    textAlign: "center" as const,
    fontFamily: "'Poppins', sans-serif",
    fontSize: "1rem",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "1.25rem",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "0.625rem",
    cursor: "pointer",
  },
  applyButton: {
    display: "flex",
    width: "4.31544rem",
    height: "2.25rem",
    padding: "0.5rem 1rem",
    justifyContent: "center",
    alignItems: "center",
    color: "#FFF",
    textAlign: "center" as const,
    fontFamily: "'Poppins', sans-serif",
    fontSize: "1rem",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "1.25rem",
    backgroundColor: "var(--primary-button, #7E57C2)",
    border: "none",
    borderRadius: "0.625rem",
    cursor: "pointer",
  },
};

export default function DateRangePicker({
  onDateRangeChange,
  initialStartDate = null,
  initialEndDate = null,
  showDefaultDates = true,
}: DateRangePickerProps) {
  const today = new Date();
  // Start date: 12 months prior, 1st date (only if showDefaultDates is true)
  const defaultStart =
    initialStartDate ||
    (showDefaultDates
      ? initialStartDate || new Date(today.getFullYear(), today.getMonth() - 12, 1)
      : null);
  // End date: Current month's last date (only if showDefaultDates is true)
  const defaultEnd =
    initialEndDate ||
    (showDefaultDates
      ? initialEndDate || new Date(today.getFullYear(), today.getMonth() + 1, 0)
      : null);

  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(defaultStart);
  const [endDate, setEndDate] = useState<Date | null>(defaultEnd);
  const [tempStart, setTempStart] = useState<Date | null>(defaultStart);
  const [tempEnd, setTempEnd] = useState<Date | null>(defaultEnd);
  const [currentMonth, setCurrentMonth] = useState(defaultStart ? defaultStart.getMonth() : today.getMonth());
  const [currentYear, setCurrentYear] = useState(defaultStart ? defaultStart.getFullYear() : today.getFullYear());
  const [selectingStart, setSelectingStart] = useState(true);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [startInputValue, setStartInputValue] = useState(
    defaultStart ? formatDate(defaultStart) : ""
  );
  const [endInputValue, setEndInputValue] = useState(
    defaultEnd ? formatDate(defaultEnd) : ""
  );
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const [startCalendarPosition, setStartCalendarPosition] = useState<{
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  }>({});
  const [endCalendarPosition, setEndCalendarPosition] = useState<{
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  }>({});

  const startFieldRef = useRef<HTMLDivElement>(null);
  const endFieldRef = useRef<HTMLDivElement>(null);
  const startCalendarRef = useRef<HTMLDivElement>(null);
  const endCalendarRef = useRef<HTMLDivElement>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate calendar popup position to stay within viewport
  const calculateCalendarPosition = (
    fieldRef: React.RefObject<HTMLDivElement>,
    calendarRef: React.RefObject<HTMLDivElement>
  ) => {
    if (!fieldRef.current) return {};

    const fieldRect = fieldRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;
    const calendarWidth = 280; // Approximate calendar width
    const calendarHeight = 280; // Approximate calendar height
    const margin = 8;

    const position: {
      top?: string;
      bottom?: string;
      left?: string;
      right?: string;
      transform?: string;
    } = {};

    // Calculate available space
    const spaceRight = viewportWidth - fieldRect.right;
    const spaceLeft = fieldRect.left;
    const spaceBelow = viewportHeight - fieldRect.bottom;
    const spaceAbove = fieldRect.top;

    // Horizontal positioning
    if (spaceRight < calendarWidth + margin && spaceLeft > spaceRight) {
      // Not enough space on right, more space on left - align to right edge of field
      position.right = "0";
      position.left = "auto";
    } else if (spaceLeft < calendarWidth + margin) {
      // Not enough space on left - align to left edge of viewport
      position.left = `${-fieldRect.left + margin + scrollX}px`;
      position.right = "auto";
    } else {
      // Default: align to left edge of field
      position.left = "0";
      position.right = "auto";
    }

    // Vertical positioning
    if (spaceBelow < calendarHeight + margin && spaceAbove > spaceBelow) {
      // Not enough space below, more space above - show above field
      position.bottom = "100%";
      position.top = "auto";
    } else {
      // Default: show below field
      position.top = "100%";
      position.bottom = "auto";
    }

    return position;
  };

  // Update calendar positions when they open
  useEffect(() => {
    if (showStartCalendar && startFieldRef.current) {
      const position = calculateCalendarPosition(startFieldRef, startCalendarRef);
      setStartCalendarPosition(position);
    }
  }, [showStartCalendar]);

  useEffect(() => {
    if (showEndCalendar && endFieldRef.current) {
      const position = calculateCalendarPosition(endFieldRef, endCalendarRef);
      setEndCalendarPosition(position);
    }
  }, [showEndCalendar]);

  // Handle window resize to recalculate positions
  useEffect(() => {
    const handleResize = () => {
      if (showStartCalendar) {
        const position = calculateCalendarPosition(startFieldRef, startCalendarRef);
        setStartCalendarPosition(position);
      }
      if (showEndCalendar) {
        const position = calculateCalendarPosition(endFieldRef, endCalendarRef);
        setEndCalendarPosition(position);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [showStartCalendar, showEndCalendar]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowStartCalendar(false);
        setShowEndCalendar(false);
        setTempStart(startDate);
        setTempEnd(endDate);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [startDate, endDate]);

  const handleOpen = () => {
    setIsOpen(true);
    setTempStart(startDate);
    setTempEnd(endDate);
    setSelectingStart(true);
  };

  const handleStartDayClick = (day: number) => {
    const selectedDate = new Date(currentYear, currentMonth, day);
    if (isFutureDate(selectedDate)) return;

    setTempStart(selectedDate);
    setStartInputValue(formatDate(selectedDate));
    setShowStartCalendar(false);

    // Clear end date error if new start is valid
    if (tempEnd && selectedDate <= tempEnd) {
      setEndDateError(null);
    } else if (tempEnd && selectedDate > tempEnd) {
      setTempEnd(null);
      setEndInputValue("");
      setEndDateError(null);
    }
  };

  const handleEndDayClick = (day: number) => {
    const selectedDate = new Date(currentYear, currentMonth, day);
    if (isFutureDate(selectedDate)) return;
    if (isBeforeDate(selectedDate, tempStart)) {
      setEndDateError("End date must be equal to or after start date");
      return;
    }

    setTempEnd(selectedDate);
    setEndInputValue(formatDate(selectedDate));
    setEndDateError(null);
    setShowEndCalendar(false);
  };

  const parseInputDate = (value: string): Date | null => {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
    return null;
  };

  const handleStartInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setStartInputValue(value);
    const parsed = parseInputDate(value);
    if (parsed && !isFutureDate(parsed)) {
      setTempStart(parsed);
      setCurrentMonth(parsed.getMonth());
      setCurrentYear(parsed.getFullYear());
      if (tempEnd && parsed > tempEnd) {
        setTempEnd(null);
        setEndInputValue("");
      }
    }
  };

  const handleEndInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEndInputValue(value);
    const parsed = parseInputDate(value);
    if (parsed) {
      if (isFutureDate(parsed)) {
        setEndDateError("End date cannot be in the future");
        return;
      }
      if (isBeforeDate(parsed, tempStart)) {
        setEndDateError("End date must be equal to or after start date");
        return;
      }
      setTempEnd(parsed);
      setEndDateError(null);
    }
  };

  const handleStartFieldClick = () => {
    setShowStartCalendar(true);
    setShowEndCalendar(false);
    if (tempStart) {
      setCurrentMonth(tempStart.getMonth());
      setCurrentYear(tempStart.getFullYear());
    }
  };

  const handleEndFieldClick = () => {
    setShowEndCalendar(true);
    setShowStartCalendar(false);
    if (tempEnd) {
      setCurrentMonth(tempEnd.getMonth());
      setCurrentYear(tempEnd.getFullYear());
    } else if (tempStart) {
      setCurrentMonth(tempStart.getMonth());
      setCurrentYear(tempStart.getFullYear());
    }
  };

  const handleApply = () => {
    if (endDateError) return;
    if (tempEnd && tempStart && tempEnd < tempStart) {
      setEndDateError("End date must be equal to or after start date");
      return;
    }
    setStartDate(tempStart);
    setEndDate(tempEnd);
    onDateRangeChange(tempStart, tempEnd);
    setIsOpen(false);
    setShowStartCalendar(false);
    setShowEndCalendar(false);
  };

  const handleCancel = () => {
    // Clear dates to show all records
    setStartDate(null);
    setEndDate(null);
    setTempStart(null);
    setTempEnd(null);
    setStartInputValue("");
    setEndInputValue("");
    setEndDateError(null);
    setIsOpen(false);
    setShowStartCalendar(false);
    setShowEndCalendar(false);
    // Notify parent to clear date range
    onDateRangeChange(null, null);
  };

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const days: (number | null)[] = [];

  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  return (
    <div ref={containerRef} style={styles.container}>
      {/* Trigger Button */}
      <button
        onClick={handleOpen}
        style={{
          ...styles.trigger,
          borderColor:
            hoveredButton === "trigger" ? "rgba(126,87,194,0.5)" : "#d0d5db",
        }}
        onMouseEnter={() => setHoveredButton("trigger")}
        onMouseLeave={() => setHoveredButton(null)}
      >
        <CalendarIcon size={18} color="#9ca3af" />
        <span style={styles.triggerText}>
          {startDate && endDate
            ? `${formatDate(startDate)} - ${formatDate(endDate)}`
            : "Pick a date range"}
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div style={styles.dropdown}>
          <div style={styles.dropdownContent}>
            {/* Start Date Field */}
            <div style={styles.fieldWrapper} ref={startFieldRef}>
              <div style={styles.fieldLabel}>Start Date</div>
              <div
                style={{
                  ...styles.fieldInput,
                  borderColor: showStartCalendar ? "#7e57c2" : "#D1D5DC",
                  cursor: "pointer",
                }}
                onClick={handleStartFieldClick}
              >
                <input
                  type="text"
                  value={startInputValue}
                  onChange={handleStartInputChange}
                  placeholder="e.g. Jan 1, 2024"
                  style={styles.fieldTextInput}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={handleStartFieldClick}
                />
                <CalendarIcon size={14} color="black" />
              </div>

              {/* Start Date Calendar Popup */}
              {showStartCalendar && (
                <div
                  ref={startCalendarRef}
                  style={{
                    ...styles.calendarPopup,
                    ...startCalendarPosition,
                    marginTop: startCalendarPosition.bottom ? "0.5rem" : "0.8rem",
                    marginBottom: startCalendarPosition.bottom ? "0.8rem" : "0",
                  }}
                >
                  <div style={styles.calendarHeader}>
                    <button
                      onClick={prevMonth}
                      style={{
                        ...styles.navButton,
                        backgroundColor:
                          hoveredButton === "prev-start"
                            ? "#f3f4f6"
                            : "transparent",
                      }}
                      onMouseEnter={() => setHoveredButton("prev-start")}
                      onMouseLeave={() => setHoveredButton(null)}
                    >
                      <ChevronLeft
                        size={20}
                        color={
                          hoveredButton === "prev-start" ? "#7e57c2" : "#6b7280"
                        }
                      />
                    </button>
                    <span style={styles.monthLabel}>
                      {MONTHS[currentMonth]} {currentYear}
                    </span>
                    <button
                      onClick={nextMonth}
                      style={{
                        ...styles.navButton,
                        backgroundColor:
                          hoveredButton === "next-start"
                            ? "#f3f4f6"
                            : "transparent",
                      }}
                      onMouseEnter={() => setHoveredButton("next-start")}
                      onMouseLeave={() => setHoveredButton(null)}
                    >
                      <ChevronRight
                        size={20}
                        color={
                          hoveredButton === "next-start" ? "#7e57c2" : "#6b7280"
                        }
                      />
                    </button>
                  </div>
                  <div style={styles.daysGrid}>
                    {DAYS.map((d) => (
                      <div key={d} style={styles.dayHeader}>
                        {d}
                      </div>
                    ))}
                  </div>
                  <div style={styles.daysGrid}>
                    {days.map((day, index) => {
                      if (day === null) {
                        return (
                          <div
                            key={`empty-${index}`}
                            style={{ width: "36px", height: "36px" }}
                          />
                        );
                      }
                      const date = new Date(currentYear, currentMonth, day);
                      const isDisabled = isFutureDate(date);
                      const isStart = isSameDay(date, tempStart);
                      const isHovered = hoveredButton === `start-day-${day}`;

                      let dayStyle: CSSProperties = {
                        ...styles.dayCell,
                        ...styles.dayCellDefault,
                      };
                      if (isDisabled) {
                        dayStyle = {
                          ...styles.dayCell,
                          ...styles.dayCellDisabled,
                        };
                      } else if (isStart) {
                        dayStyle = {
                          ...styles.dayCell,
                          ...styles.dayCellSelected,
                        };
                      } else if (isHovered) {
                        dayStyle = {
                          ...styles.dayCell,
                          backgroundColor: "#ede7f6",
                          color: "#354152",
                        };
                      }

                      return (
                        <button
                          key={day}
                          onClick={() =>
                            !isDisabled && handleStartDayClick(day)
                          }
                          style={dayStyle}
                          onMouseEnter={() =>
                            !isDisabled && setHoveredButton(`start-day-${day}`)
                          }
                          onMouseLeave={() => setHoveredButton(null)}
                          disabled={isDisabled}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* End Date Field */}
            <div style={styles.fieldWrapper} ref={endFieldRef}>
              <div style={styles.fieldLabel}>End Date</div>
              <div
                style={{
                  ...styles.fieldInput,
                  borderColor: showEndCalendar
                    ? "#7e57c2"
                    : endDateError
                    ? "#ef4444"
                    : "#D1D5DC",
                  cursor: "pointer",
                }}
                onClick={handleEndFieldClick}
              >
                <input
                  type="text"
                  value={endInputValue}
                  onChange={handleEndInputChange}
                  placeholder="e.g. Jan 31, 2024"
                  style={styles.fieldTextInput}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={handleEndFieldClick}
                />
                <CalendarIcon size={14} color="black" />
              </div>
              {endDateError && (
                <div style={styles.errorText}>{endDateError}</div>
              )}

              {/* End Date Calendar Popup */}
              {showEndCalendar && (
                <div
                  ref={endCalendarRef}
                  style={{
                    ...styles.calendarPopup,
                    ...endCalendarPosition,
                    marginTop: endCalendarPosition.bottom ? "0.5rem" : "0.8rem",
                    marginBottom: endCalendarPosition.bottom ? "0.8rem" : "0",
                  }}
                >
                  <div style={styles.calendarHeader}>
                    <button
                      onClick={prevMonth}
                      style={{
                        ...styles.navButton,
                        backgroundColor:
                          hoveredButton === "prev-end"
                            ? "#f3f4f6"
                            : "transparent",
                      }}
                      onMouseEnter={() => setHoveredButton("prev-end")}
                      onMouseLeave={() => setHoveredButton(null)}
                    >
                      <ChevronLeft
                        size={20}
                        color={
                          hoveredButton === "prev-end" ? "#7e57c2" : "#6b7280"
                        }
                      />
                    </button>
                    <span style={styles.monthLabel}>
                      {MONTHS[currentMonth]} {currentYear}
                    </span>
                    <button
                      onClick={nextMonth}
                      style={{
                        ...styles.navButton,
                        backgroundColor:
                          hoveredButton === "next-end"
                            ? "#f3f4f6"
                            : "transparent",
                      }}
                      onMouseEnter={() => setHoveredButton("next-end")}
                      onMouseLeave={() => setHoveredButton(null)}
                    >
                      <ChevronRight
                        size={20}
                        color={
                          hoveredButton === "next-end" ? "#7e57c2" : "#6b7280"
                        }
                      />
                    </button>
                  </div>
                  <div style={styles.daysGrid}>
                    {DAYS.map((d) => (
                      <div key={d} style={styles.dayHeader}>
                        {d}
                      </div>
                    ))}
                  </div>
                  <div style={styles.daysGrid}>
                    {days.map((day, index) => {
                      if (day === null) {
                        return (
                          <div
                            key={`empty-${index}`}
                            style={{ width: "36px", height: "36px" }}
                          />
                        );
                      }
                      const date = new Date(currentYear, currentMonth, day);
                      const isDisabled =
                        isFutureDate(date) || isBeforeDate(date, tempStart);
                      const isEnd = isSameDay(date, tempEnd);
                      const inRange = isInRange(date, tempStart, tempEnd);
                      const isHovered = hoveredButton === `end-day-${day}`;

                      let dayStyle: CSSProperties = {
                        ...styles.dayCell,
                        ...styles.dayCellDefault,
                      };
                      if (isDisabled) {
                        dayStyle = {
                          ...styles.dayCell,
                          ...styles.dayCellDisabled,
                        };
                      } else if (isEnd) {
                        dayStyle = {
                          ...styles.dayCell,
                          ...styles.dayCellSelected,
                        };
                      } else if (inRange) {
                        dayStyle = {
                          ...styles.dayCell,
                          ...styles.dayCellInRange,
                        };
                      } else if (isHovered) {
                        dayStyle = {
                          ...styles.dayCell,
                          backgroundColor: "#ede7f6",
                          color: "#354152",
                        };
                      }

                      return (
                        <button
                          key={day}
                          onClick={() => !isDisabled && handleEndDayClick(day)}
                          style={dayStyle}
                          onMouseEnter={() =>
                            !isDisabled && setHoveredButton(`end-day-${day}`)
                          }
                          onMouseLeave={() => setHoveredButton(null)}
                          disabled={isDisabled}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Action Buttons */}
          <div style={styles.actionBar}>
            <button
              onClick={handleCancel}
              style={styles.cancelButton}
              onMouseEnter={() => setHoveredButton("cancel")}
              onMouseLeave={() => setHoveredButton(null)}
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              style={styles.applyButton}
              onMouseEnter={() => setHoveredButton("apply")}
              onMouseLeave={() => setHoveredButton(null)}
            >
              Apply
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
